import type { AdminStayStatus } from '@/api/core/stays';
import { formatDateTimeWithOffset } from '@/lib/format';
import { Badge } from '@mgrvtechgroup/admin-ds';

const stayStatusLabels: Record<AdminStayStatus, string> = {
  PREPARING: '준비 중',
  READY: '준비 완료',
  CHECKED_IN: '체크인',
  CHECKED_OUT: '체크아웃',
  CANCELLED: '취소',
  NO_SHOW: '노쇼',
  TERMINATED: '종료',
};

export function displayStayStatus(status: string): string {
  return Object.hasOwn(stayStatusLabels, status)
    ? stayStatusLabels[status as AdminStayStatus]
    : status;
}

const stayStatusTones: Record<
  AdminStayStatus,
  'info' | 'success' | 'warning' | 'danger' | 'neutral'
> = {
  PREPARING: 'warning',
  READY: 'info',
  CHECKED_IN: 'success',
  CHECKED_OUT: 'neutral',
  CANCELLED: 'danger',
  NO_SHOW: 'danger',
  TERMINATED: 'neutral',
};

export function StayStatusBadge({ status }: { status: string }) {
  const tone = Object.hasOwn(stayStatusTones, status)
    ? stayStatusTones[status as AdminStayStatus]
    : 'neutral';
  return <Badge tone={tone}>{displayStayStatus(status)}</Badge>;
}

export function StayPeriod({
  startAt,
  endAt,
  inline = false,
}: {
  startAt: string;
  endAt: string;
  /** 목록 행처럼 높이를 아껴야 할 때 시작 → 종료를 한 줄로 보여 준다. */
  inline?: boolean;
}) {
  const start = (
    <time
      dateTime={startAt}
      aria-label={`시작 ${startAt}`}
      className="whitespace-nowrap tabular-nums"
    >
      {formatDateTimeWithOffset(startAt)}
    </time>
  );
  const end = (
    <time dateTime={endAt} aria-label={`종료 ${endAt}`} className="whitespace-nowrap tabular-nums">
      {formatDateTimeWithOffset(endAt)}
    </time>
  );
  if (inline)
    return (
      <span className="flex flex-wrap items-center gap-x-2 whitespace-normal">
        {start}
        <span aria-hidden="true">→</span>
        {end}
      </span>
    );
  return (
    <span className="grid gap-1 whitespace-normal">
      <span>
        <span aria-hidden="true">시작 </span>
        {start}
      </span>
      <span>
        <span aria-hidden="true">종료 </span>
        {end}
      </span>
    </span>
  );
}
